import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Users, Search, Package, Bot } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

type Extension = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  full_description: string | null;
  icon_name: string;
  source_path: string;
  pricing_type: string;
  price_cents: number;
  is_active: boolean;
  is_suspended: boolean;
  suspension_message: string | null;
  ai_service_id: string | null;
  ai_services?: { name: string; provider: string } | null;
};

type HealthArea = {
  id: string;
  name: string;
};

type AiService = {
  id: string;
  name: string;
  provider: string;
};

const AVAILABLE_ICONS = [
  "Heart", "HeartPulse", "Stethoscope", "Pill", "Syringe", "Thermometer",
  "Activity", "Brain", "Eye", "Ear", "Bone", "Dna",
  "Apple", "Salad", "Utensils", "UtensilsCrossed", "Coffee", "Cookie",
  "Beef", "Egg", "Fish", "Grape", "Carrot", "Leaf",
  "Dumbbell", "Timer", "Footprints", "PersonStanding", "Bike", "Mountain",
  "FileText", "ClipboardList", "ClipboardCheck", "Notebook", "BookOpen", "Receipt",
  "Calendar", "CalendarDays", "Clock", "AlarmClock",
  "BarChart", "LineChart", "PieChart", "TrendingUp", "Target",
  "Package", "Grid", "Layers", "Sparkles", "Star", "Zap",
  "Calculator", "Scale", "Ruler", "Microscope", "TestTube",
  "Users", "User", "UserCheck", "Baby", "Cross",
];

export default function ConfiguracoesAplicativos() {
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [healthAreas, setHealthAreas] = useState<HealthArea[]>([]);
  const [aiServices, setAiServices] = useState<AiService[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const [iconSearchQuery, setIconSearchQuery] = useState("");
  const [editingExtension, setEditingExtension] = useState<Extension | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    short_description: "",
    full_description: "",
    icon_name: "Package",
    source_path: "",
    pricing_type: "free",
    price_cents: 0,
    is_active: true,
    is_suspended: false,
    suspension_message: "",
    selected_areas: [] as string[],
    ai_service_id: "" as string | null,
  });

  useEffect(() => {
    loadExtensions();
    loadHealthAreas();
    loadAiServices();
  }, []);

  const loadExtensions = async () => {
    try {
      const { data, error } = await supabase
        .from("extensions")
        .select("*, ai_services(name, provider)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setExtensions(data || []);
    } catch (error: any) {
      toast.error("Erro ao carregar aplicativos: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHealthAreas = async () => {
    try {
      const { data, error } = await supabase
        .from("health_areas")
        .select("id, name")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setHealthAreas(data || []);
    } catch (error: any) {
      toast.error("Erro ao carregar áreas: " + error.message);
    }
  };

  const loadAiServices = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_services")
        .select("id, name, provider")
        .eq("is_active", true)
        .order("provider, name");

      if (error) throw error;
      setAiServices(data || []);
    } catch (error: any) {
      toast.error("Erro ao carregar serviços de IA: " + error.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const extensionData = {
        title: formData.title,
        slug: formData.slug,
        short_description: formData.short_description,
        full_description: formData.full_description,
        icon_name: formData.icon_name,
        source_path: formData.source_path,
        pricing_type: formData.pricing_type,
        price_cents: formData.price_cents,
        is_active: formData.is_active,
        is_suspended: formData.is_suspended,
        suspension_message: formData.suspension_message,
        ai_service_id: formData.ai_service_id || null,
      };

      if (editingExtension) {
        const { error } = await supabase
          .from("extensions")
          .update(extensionData)
          .eq("id", editingExtension.id);

        if (error) throw error;

        // Update health areas
        await supabase
          .from("extension_health_areas")
          .delete()
          .eq("extension_id", editingExtension.id);

        if (formData.selected_areas.length > 0) {
          const areas = formData.selected_areas.map((area_id) => ({
            extension_id: editingExtension.id,
            health_area_id: area_id,
          }));

          await supabase.from("extension_health_areas").insert(areas);
        }

        toast.success("Aplicativo atualizado com sucesso!");
      } else {
        const { data: newExtension, error } = await supabase
          .from("extensions")
          .insert(extensionData)
          .select()
          .single();

        if (error) throw error;

        // Insert health areas
        if (formData.selected_areas.length > 0) {
          const areas = formData.selected_areas.map((area_id) => ({
            extension_id: newExtension.id,
            health_area_id: area_id,
          }));

          await supabase.from("extension_health_areas").insert(areas);
        }

        toast.success("Aplicativo criado com sucesso!");
      }

      setDialogOpen(false);
      resetForm();
      loadExtensions();
    } catch (error: any) {
      toast.error("Erro ao salvar aplicativo: " + error.message);
    }
  };

  const handleEdit = async (extension: Extension) => {
    setEditingExtension(extension);

    // Load selected health areas
    const { data: areas } = await supabase
      .from("extension_health_areas")
      .select("health_area_id")
      .eq("extension_id", extension.id);

    setFormData({
      title: extension.title,
      slug: extension.slug,
      short_description: extension.short_description,
      full_description: extension.full_description || "",
      icon_name: extension.icon_name,
      source_path: extension.source_path,
      pricing_type: extension.pricing_type,
      price_cents: extension.price_cents,
      is_active: extension.is_active,
      is_suspended: extension.is_suspended,
      suspension_message: extension.suspension_message || "",
      selected_areas: areas?.map((a) => a.health_area_id) || [],
      ai_service_id: extension.ai_service_id || "",
    });

    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este aplicativo?")) return;

    try {
      const { error } = await supabase.from("extensions").delete().eq("id", id);

      if (error) throw error;
      toast.success("Aplicativo excluído com sucesso!");
      loadExtensions();
    } catch (error: any) {
      toast.error("Erro ao excluir aplicativo: " + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      short_description: "",
      full_description: "",
      icon_name: "Package",
      source_path: "",
      pricing_type: "free",
      price_cents: 0,
      is_active: true,
      is_suspended: false,
      suspension_message: "",
      selected_areas: [],
      ai_service_id: "",
    });
    setEditingExtension(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Aplicativos</h1>
          <p className="text-muted-foreground">
            Gerencie os aplicativos disponíveis para os profissionais
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Aplicativo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingExtension ? "Editar Aplicativo" : "Novo Aplicativo"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL) *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    placeholder="planos-alimentares"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="short_description">Descrição Rápida *</Label>
                <Input
                  id="short_description"
                  value={formData.short_description}
                  onChange={(e) =>
                    setFormData({ ...formData, short_description: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_description">Descrição Completa</Label>
                <Textarea
                  id="full_description"
                  value={formData.full_description}
                  onChange={(e) =>
                    setFormData({ ...formData, full_description: e.target.value })
                  }
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ícone *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setIconModalOpen(true)}
                  >
                    {(() => {
                      const IconComponent = (LucideIcons as any)[formData.icon_name];
                      return IconComponent ? (
                        <>
                          <IconComponent className="h-5 w-5 mr-2" />
                          {formData.icon_name}
                        </>
                      ) : (
                        <>
                          <Package className="h-5 w-5 mr-2" />
                          {formData.icon_name}
                        </>
                      );
                    })()}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source_path">Caminho do Código *</Label>
                  <Input
                    id="source_path"
                    value={formData.source_path}
                    onChange={(e) =>
                      setFormData({ ...formData, source_path: e.target.value })
                    }
                    placeholder="extensions/mealplan"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pricing_type">Tipo de Cobrança *</Label>
                  <Select
                    value={formData.pricing_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, pricing_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Gratuito</SelectItem>
                      <SelectItem value="one_time">Pagamento Único</SelectItem>
                      <SelectItem value="subscription">Assinatura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.pricing_type !== "free" && (
                  <div className="space-y-2">
                    <Label htmlFor="price_cents">Valor (em centavos)</Label>
                    <Input
                      id="price_cents"
                      type="number"
                      value={formData.price_cents}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price_cents: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="9900"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Áreas de Saúde Permitidas *</Label>
                <div className="grid grid-cols-2 gap-2 border rounded-md p-4">
                  {healthAreas.map((area) => (
                    <div key={area.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`area-${area.id}`}
                        checked={formData.selected_areas.includes(area.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              selected_areas: [...formData.selected_areas, area.id],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              selected_areas: formData.selected_areas.filter(
                                (id) => id !== area.id
                              ),
                            });
                          }
                        }}
                      />
                      <Label htmlFor={`area-${area.id}`} className="cursor-pointer">
                        {area.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>IA Utilizada</Label>
                <Select
                  value={formData.ai_service_id || "none"}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      ai_service_id: value === "none" ? null : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a IA..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma IA</SelectItem>
                    {aiServices.map((ai) => (
                      <SelectItem key={ai.id} value={ai.id}>
                        <div className="flex items-center gap-2">
                          <Bot className="h-4 w-4" />
                          {ai.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Ativo</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_suspended"
                    checked={formData.is_suspended}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_suspended: checked })
                    }
                  />
                  <Label htmlFor="is_suspended">Suspenso</Label>
                </div>
              </div>

              {formData.is_suspended && (
                <div className="space-y-2">
                  <Label htmlFor="suspension_message">Mensagem de Suspensão</Label>
                  <Textarea
                    id="suspension_message"
                    value={formData.suspension_message}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        suspension_message: e.target.value,
                      })
                    }
                    placeholder="Este aplicativo está temporariamente suspenso..."
                    rows={2}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingExtension ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Icon Selection Modal */}
        <Dialog open={iconModalOpen} onOpenChange={setIconModalOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Selecionar Ícone</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar ícone..."
                  value={iconSearchQuery}
                  onChange={(e) => setIconSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="grid grid-cols-6 gap-3 max-h-[50vh] overflow-y-auto p-2">
                {AVAILABLE_ICONS
                  .filter((iconName) =>
                    iconName.toLowerCase().includes(iconSearchQuery.toLowerCase())
                  )
                  .map((iconName) => {
                    const IconComponent = (LucideIcons as any)[iconName];
                    if (!IconComponent) return null;

                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, icon_name: iconName });
                          setIconModalOpen(false);
                          setIconSearchQuery("");
                        }}
                        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:border-primary hover:bg-accent ${formData.icon_name === iconName
                          ? "border-primary bg-accent"
                          : "border-border"
                          }`}
                      >
                        <IconComponent className="h-6 w-6 mb-2" />
                        <span className="text-xs text-center text-muted-foreground">
                          {iconName}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>IA</TableHead>
              <TableHead>Cobrança</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {extensions.map((ext) => (
              <TableRow key={ext.id}>
                <TableCell className="font-medium">{ext.title}</TableCell>
                <TableCell className="max-w-xl whitespace-pre-line break-words">
                  {ext.full_description?.trim() || ext.short_description}
                </TableCell>
                <TableCell>
                  {ext.ai_services ? (
                    <Badge variant="outline" className="gap-1">
                      <Bot className="h-3 w-3" />
                      {ext.ai_services.name}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {ext.pricing_type === "free" ? (
                    <Badge variant="secondary">Gratuito</Badge>
                  ) : (
                    <Badge variant="default">
                      {formatPrice(ext.price_cents)}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {ext.is_suspended ? (
                    <Badge variant="destructive">Suspenso</Badge>
                  ) : ext.is_active ? (
                    <Badge variant="default">Ativo</Badge>
                  ) : (
                    <Badge variant="secondary">Inativo</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(ext)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(ext.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
